import { inject, injectable } from "inversify";
import type { SearchFilterService, UrlService, UserTierService } from ".";
import type { IpfModule } from "..";
import { Constants } from "../constants.generated";
import { FilterType } from "../enums.generated";
import { EventConstants } from "../EventConstants";
import { applyEnterButtonTarget } from "../functions/apply-enter-button-target";
import { asNumberOrString } from "../functions/as-number-or-string";
import { logError } from "../functions/client-log";
import { TYPES } from "../ioc/types";
import { DataGridRefreshArgs } from "../models/data-grid";
import { ApiModel } from "../models/search";
import { Guid } from "../util/Guid";
import { ServiceBase } from "./ServiceBase";

/**
 * Search filter service. Enables filtering one or many data grids.
 */
@injectable()
export class IpfSearchFilterService extends ServiceBase implements SearchFilterService {

	/**
	 * Map of filter value retrievers mapped by filter type.
	 */ 
	private static readonly filterValueRetrievers = new Map<FilterType, (element: Element) => any>([
		[FilterType.TextBox, IpfSearchFilterService.getTextBoxFilterValue],
		[FilterType.DropDown, IpfSearchFilterService.getDropDownFilterValue],
		[FilterType.TierSelector, IpfSearchFilterService.getTierSelectorFilterValue],
		[FilterType.Date, IpfSearchFilterService.getTextBoxFilterValue]
	]);

	/**
	 * Map of filter value clearers mapped by filter type.
	 */
	private static readonly filterValueClearers = new Map<FilterType, (element: Element) => any>([
		[FilterType.TextBox, IpfSearchFilterService.clearTextBoxFilterValue],
		[FilterType.DropDown, IpfSearchFilterService.clearDropDownFilterValue],
		[FilterType.TierSelector, IpfSearchFilterService.clearTierSelectorFilterValue],
		[FilterType.Date, IpfSearchFilterService.clearDateFilterValue]
	]);

	private static readonly apiModels = new Map<string, ApiModel>();

	/**
	 * Initialises a new instance of the IpfSearchFilterService class.
	 * @param userTierService User tier service, used to apply tier params to URLs.
	 * @param urlService URL service, used to generate URLs.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 */
	constructor(
		@inject(TYPES.UserTierService) private userTierService: UserTierService,
		@inject(TYPES.UrlService) private urlService: UrlService,
		@inject(TYPES.IpfModule) private ipfModule: IpfModule) {
		super();
	}

	/**
	 * @inheritdoc
	 */
	public setupFilters(element: Element, onChangeHandler: (args: DataGridRefreshArgs) => void) {

		const changeArgs: DataGridRefreshArgs = { resetPageIndex: true };
		const elements = new Array<HTMLElement>();
		let inputTimeout: NodeJS.Timeout;

		// this is the onput text filter handler, which searches as the user types
		const onInput = (event: Event) => {
			if (inputTimeout) clearTimeout(inputTimeout);
			inputTimeout = global.setTimeout(() => {
				onChangeHandler(changeArgs);
				if (event.target instanceof HTMLInputElement) {
					this.insertClearFilter(event.target);
				}
			}, 500);
		};

		// this is the most commonly used on change filter,
		// which searches when the user blurs the text box, selects a date or dropdown item
		const onChange = (event: Event) => {
			onChangeHandler(changeArgs);
			if (event.target instanceof Element) {
				this.insertClearFilter(event.target);
			}
		};

		// this is the export button click handler
		const onExport = (event: Event) => {

			this.export(element, event);
		}

		// filter sets can either search on filter change or if there is a filter submit element on button click
		const submitElement = element.querySelector<HTMLElement>("[data-filter-submit]");

		let submitHandler: () => void;

		// apply filter submit behaviour
		if (submitElement) {

			const validator = () => {

				let valid = true;
				element.querySelectorAll("[data-filter-min-length]").forEach(filterElement => {

					if (valid && filterElement instanceof HTMLInputElement) {

						const minLength = parseInt(filterElement.getAttribute("data-filter-min-length") ?? "0");

						if (minLength && filterElement.value.trim().length < minLength) {
							valid = false;
						}
					}
				});

				return valid;
			}

			submitHandler = () => {

				// if filters validate, trigger the search
				if (validator() === true) {
					onChangeHandler(changeArgs);
				}
			};

			submitElement.addEventListener(EventConstants.click, submitHandler);

			elements.push(submitElement);

			if (!submitElement.id) {
				submitElement.id = Guid.newGuid();
			}

			// hook up enter pressed events on the <input> filters
			applyEnterButtonTarget(`#${element.id} input[data-filter]`, `#${submitElement.id}`, validator);
		}
		else {
			submitHandler = () => onChangeHandler(changeArgs);
		}

		const exportButton = element.querySelector<HTMLButtonElement>("[data-export]");
		if (exportButton) {

			exportButton.addEventListener(EventConstants.click, onExport);

			// if there is an export button and no submit search button then need to ensure export isn't triggered.
			// this is because the default browser behaviour is to trigger a click of the 1st submit button it finds, when text boxes are focused and enter is pressed.
			if (exportButton && !submitElement) {

				// get all date picker and standard text input fields
				element.querySelectorAll<HTMLInputElement>(".date-picker .MuiInputBase-input, input[data-filter]").forEach(dateInput => {

					// ensure there are no duplicate events firing
					dateInput.removeEventListener(EventConstants.keydown, this.handleInputFilterEnter);

					// handle keydown event
					dateInput.addEventListener(EventConstants.keydown, this.handleInputFilterEnter);
				});
			}
		}

		element.querySelectorAll("[data-filter]").forEach(filterElement => {

			if (filterElement instanceof HTMLInputElement || filterElement instanceof HTMLSelectElement) {

				const event = filterElement.getAttribute("data-filter-event");

				// attach on change/input events to the filter elements
				if (event !== EventConstants.none) {

					filterElement.addEventListener(event ?? EventConstants.change, event === EventConstants.input ? onInput : onChange);
					elements.push(filterElement);
				}

				this.insertClearFilter(filterElement);
			}

			const filterType = this.getFilterType(filterElement);

			if (filterType === FilterType.TierSelector && filterElement.getAttribute("data-tier-root") === Constants.TierKeyPageTier) {

				// subscribe to the page tier change event
				this.userTierService.subscribe(element.id, () => {
					const clearFilter = filterElement.parentElement?.querySelector(".clear-filter");
					clearFilter?.dispatchEvent(new Event(EventConstants.click));
				});
			}
		});

		// return tear down function which removes events
		return () => {

			elements.forEach(filterElement => {

				if (filterElement.hasAttribute("data-filter-submit")) {
					filterElement.removeEventListener(EventConstants.click, submitHandler);
				}
				else {
					const event = filterElement.getAttribute("data-filter-event");
					filterElement.removeEventListener(event ?? EventConstants.change, event === EventConstants.input ? onInput : onChange);
				}
			});

			if (exportButton) {

				exportButton.removeEventListener(EventConstants.click, onExport);
			}
		};
	}

	/**
	 * @inheritdoc
	 */
	public applyFilters(filtersElement: Element, filters: object, apiModel?: ApiModel) {

		const filterData = filters as any;

		filtersElement.querySelectorAll("[data-filter]").forEach(filterElement => {

			const filterName = this.getFilterName(filterElement);
			const filterType = this.getFilterType(filterElement);

			// get the filter value retriever and set filter model property
			const valueRetriever = IpfSearchFilterService.filterValueRetrievers.get(filterType);

			if (valueRetriever) {

				const filterValue = valueRetriever(filterElement);
				if (filterValue) {

					filterData[filterName] = filterValue;
				}
				else {

					filterData[filterName] = undefined;
				}
			}
		});

		if (apiModel) {

			IpfSearchFilterService.apiModels.set(filtersElement.id, apiModel);
		}
	}

	private export(filtersElement: Element, event: Event) {

		// get the API model, which will be used to pass through the JSON filters through in a hidden field
		// as the file download request is not called with AJAX
		const apiModel = IpfSearchFilterService.apiModels.get(filtersElement.id);

		if (apiModel) {

			// get the closest ancestor form, requires the filters are within a form
			const form = filtersElement.closest("form");

			if (form) {

				const btn = event.currentTarget;

				// set the form or button action
				if (btn instanceof HTMLButtonElement && btn.formAction) {

					let formAction: string;
					let origFormAction = btn.getAttribute("data-original-form-action");
					if (origFormAction) {

						formAction = origFormAction;
					}
					else {

						btn.setAttribute("data-original-form-action", btn.formAction);
						formAction = btn.formAction;
					}

					btn.formAction = this.urlService.getPageTierUrl(formAction);
				}
				else {

					form.action = this.urlService.getPageTierUrl(form.action);
				}

				try {

					// find the export query field and serialise the JSON search model to its value
					const queryField = form.querySelector<HTMLInputElement>(`[name=${Constants.ExportQueryFieldName}]`);
					if (queryField) {
						queryField.value = JSON.stringify(apiModel);
					}
					else if (this.ipfModule.context.clientDebugEnabled) {
						logError("Export query field was not found.")
					}
				}
				catch (error) {

					event.preventDefault();

					if (this.ipfModule.context.clientDebugEnabled) {
						logError("Error on export.");
						throw error;
					}
				}
			}
		}

		// now let the event carry on and submit the form and perform the export...
	}

	private static getTextBoxFilterValue(element: Element) {

		if (element instanceof HTMLInputElement) {

			const input = element as HTMLInputElement;
			return input.value;
		}
		return null;
	}

	private static clearTextBoxFilterValue(element: Element) {

		if (element instanceof HTMLInputElement) {

			const input = element as HTMLInputElement;
			input.value = "";
		}
	}

	private static getTierSelectorFilterValue(element: Element) {

		if (element instanceof HTMLInputElement) {

			const input = element as HTMLInputElement;
			return input.value ? JSON.parse(input.value) : null;
		}
		return null;
	}

	private static clearTierSelectorFilterValue(element: Element) {

		if (element instanceof HTMLInputElement) {

			const input = element as HTMLInputElement;
			input.value = "";

			const textInput = input.parentElement?.querySelector("input[type=text]");

			if (textInput instanceof HTMLInputElement) {

				textInput.value = "";
				textInput.dispatchEvent(new Event(EventConstants.change));
			}
		}
	}

	private static clearDateFilterValue(element: Element) {

		if (element instanceof HTMLInputElement) {

			const input = element as HTMLInputElement;
			input.value = "";

			// get the MUI text box 
			const muiInput = input.closest(".input-group")?.querySelector(".MuiInputBase-input");

			if (muiInput instanceof HTMLInputElement) {

				// clear it and trigger change event
				muiInput.value = "";
				input.dispatchEvent(new Event(EventConstants.change_ipffilter));
			}
		}
	}

	private static getDropDownFilterValue(element: Element) {

		if (element instanceof HTMLSelectElement) {

			const select = element as HTMLSelectElement;
			if (select.selectedOptions.length) {

				return asNumberOrString(select.selectedOptions[0].value);
			}
		}
		return null;
	}

	private static clearDropDownFilterValue(element: Element) {

		if (element instanceof HTMLSelectElement) {

			const select = element as HTMLSelectElement;
			select.selectedIndex = 0;
		}
	}

	private insertClearFilter(filterElement: Element) {

		// ensure this filter can be cleared first
		if (filterElement.hasAttribute("data-filter-disable-remove")) {
			return;
		}

		const filterType = this.getFilterType(filterElement);
		const hasValue = (filterElement instanceof HTMLInputElement && filterElement.value.trim().length > 0 && (filterType !== FilterType.TierSelector || filterElement.value !== "{}")) ||
			(filterElement instanceof HTMLSelectElement && filterElement.selectedIndex > 0);

		if (filterElement.parentElement) {

			// check for existing clear button
			let clearFilter = filterElement.parentElement.querySelector(".clear-filter") as HTMLButtonElement;

			// add the clear button, if the filter has a value and there is no clear button
			if (hasValue && !clearFilter) {

				// create clear filter button with an icon as its content
				clearFilter = document.createElement("button") as HTMLButtonElement;
				clearFilter.type = "button";
				clearFilter.className = "clear-filter";
				clearFilter.title = "Remove filter";
				clearFilter.innerHTML = "<i class=\"fas fa-times\"></i>";
				filterElement.parentElement.appendChild(clearFilter);

				// get the filter clearer
				const valueClearer = IpfSearchFilterService.filterValueClearers.get(filterType);

				if (valueClearer) {

					const event = filterElement.getAttribute("data-filter-event");

					// add click event to the clear filter button
					clearFilter.addEventListener(EventConstants.click, () => {
						valueClearer(filterElement);
						clearFilter.style.display = "none";
						filterElement.dispatchEvent(new Event(event ?? EventConstants.change));
					});
				}
			}
			// already has a clear button, just show it
			else if (hasValue && clearFilter) {

				clearFilter.style.display = "block";
			}
			// no value so hide the clear button
			else if (clearFilter) {

				clearFilter.style.display = "none";
			}
		}
	}

	private getFilterType(filterElement: Element) {
		const filterTypeString = filterElement.getAttribute("data-filter") ?? "";
		return parseInt(filterTypeString) as FilterType;
	}

	private getFilterName(filterElement: Element) {
		return (filterElement.hasAttribute("data-filter-name") ?
			filterElement.getAttribute("data-filter-name") : filterElement.getAttribute("name")) ?? "";
	}

	private handleInputFilterEnter(event: KeyboardEvent) {

		if (event.key === EventConstants.key_enter) {

			event.preventDefault();
		}
	}
}